import React, { useState } from 'react';
import {
  Button,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  CircularProgress,
  Typography,
  Box,
} from '@mui/material';
import {
  Folder as FolderIcon,
  Add as AddIcon,
  Check as CheckIcon,
  FolderOpen as FolderOpenIcon,
} from '@mui/icons-material';
import { useMyCollections, useAddImageToCollection, useRemoveImageFromCollection } from '../hooks/useQueries';

interface AddToCollectionButtonProps {
  imageId: number;
  onSuccess?: () => void;
}

const AddToCollectionButton: React.FC<AddToCollectionButtonProps> = ({ imageId, onSuccess }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [addingTo, setAddingTo] = useState<number | null>(null);

  const open = Boolean(anchorEl);
  const { data: collections = [], isLoading: loading } = useMyCollections();
  const addImageMutation = useAddImageToCollection();
  const removeImageMutation = useRemoveImageFromCollection();
  const [addedTo, setAddedTo] = useState<Set<number>>(new Set());

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleAddToCollection = async (collectionId: number) => {
    try {
      setAddingTo(collectionId);
      if (addedTo.has(collectionId)) {
        await removeImageMutation.mutateAsync({ collectionId, imageId });
        setAddedTo(prev => {
          const next = new Set(prev);
          next.delete(collectionId);
          return next;
        });
      } else {
        await addImageMutation.mutateAsync({ collectionId, imageId });
        setAddedTo(prev => new Set(prev).add(collectionId));
      }
      onSuccess?.();
    } catch (err) {
      console.error('Failed to update collection:', err);
    } finally {
      setAddingTo(null);
    }
  };

  return (
    <>
      <Button
        variant="outlined"
        startIcon={<FolderIcon />}
        onClick={handleClick}
      >
        Add to Collection
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        PaperProps={{ sx: { minWidth: 250, maxHeight: 400 } }}
      >
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
            <CircularProgress size={24} />
          </Box>
        ) : collections.length === 0 ? (
          <Box sx={{ p: 2, textAlign: 'center' }}>
            <FolderOpenIcon sx={{ fontSize: 40, color: 'text.secondary', mb: 1 }} />
            <Typography variant="body2" color="text.secondary">
              No collections yet
            </Typography>
            <Button
              size="small"
              href="/collections"
              sx={{ mt: 1 }}
            >
              Create Collection
            </Button>
          </Box>
        ) : (
          collections.map(collection => (
            <MenuItem
              key={collection.id}
              onClick={() => handleAddToCollection(collection.id)}
              disabled={addingTo === collection.id}
            >
              <ListItemIcon>
                {addingTo === collection.id ? (
                  <CircularProgress size={20} />
                ) : addedTo.has(collection.id) ? (
                  <CheckIcon color="success" />
                ) : (
                  <FolderIcon sx={{ color: collection.color || 'inherit' }} />
                )}
              </ListItemIcon>
              <ListItemText
                primary={collection.name}
                secondary={`${collection.imageCount} images`}
              />
            </MenuItem>
          ))
        )}
      </Menu>
    </>
  );
};

export default AddToCollectionButton;
